import { API } from "@/lib/api";
import { PREVIEW } from "@/constants/testIds";
import { Lock, Package } from "lucide-react";

/**
 * Previews the product media publicly (limited bytes for audio/video).
 * Ebooks & bundles are locked behind purchase.
 */
export default function PreviewMedia({ product }) {
  const src = `${API}/preview/${product.id}`;
  const cat = product.category;

  if (cat === "ebook") {
    return (
      <div
        data-testid={PREVIEW.ebook}
        className="border border-white/10 bg-[#0A0A0A] p-8 flex items-center gap-4"
      >
        <Lock className="w-6 h-6 text-[#CCFF00]" />
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#CCFF00]">// PDF LOCKED</div>
          <div className="mt-1 text-white/80">Buy this drop to unlock the full PDF.</div>
        </div>
      </div>
    );
  }

  if (cat === "bundle") {
    const fc = product.bundle_file_count;
    const totalSize = formatSize(product.file_size);
    return (
      <div
        data-testid="preview-bundle-locked"
        className="border border-white/10 bg-[#0A0A0A] p-8"
      >
        <div className="flex items-center gap-4">
          <Package className="w-8 h-8 text-[#CCFF00] shrink-0" />
          <div className="min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#CCFF00]">// BUNDLE · ZIP ARCHIVE</div>
            <div className="mt-1 text-white/80">Buy this drop to unlock the full archive.</div>
          </div>
        </div>
        <div className="mt-6 grid grid-cols-2 gap-3">
          <div className="border border-white/10 py-4 text-center">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">Files</div>
            <div data-testid="bundle-file-count" className="font-mono text-lg mt-1 text-white">
              {typeof fc === "number" ? fc : "—"}
            </div>
          </div>
          <div className="border border-white/10 py-4 text-center">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">Total size</div>
            <div className="font-mono text-lg mt-1 text-white">{totalSize}</div>
          </div>
        </div>
      </div>
    );
  }

  if (cat === "podcast" || cat === "music") {
    return (
      <div className="border border-white/10 bg-[#0A0A0A] p-5">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#CCFF00] mb-3">
          // AUDIO PREVIEW · Limited clip
        </div>
        <audio
          data-testid={PREVIEW.audio}
          controls
          src={src}
          preload="metadata"
          className="w-full"
        >
          Your browser does not support the audio element.
        </audio>
      </div>
    );
  }

  if (cat === "video") {
    return (
      <div className="border border-white/10 bg-[#0A0A0A]">
        <div className="px-5 pt-5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#CCFF00]">
          // VIDEO PREVIEW · Limited clip
        </div>
        <video
          data-testid={PREVIEW.video}
          controls
          src={src}
          preload="metadata"
          className="w-full mt-3"
        />
      </div>
    );
  }

  // image (public preview: server-side downscaled + watermarked in
  // /api/preview/{id}; the un-watermarked original is only served via
  // /api/files/download/{id} to the creator or paid buyer)
  return (
    <div className="border border-white/10 bg-[#0A0A0A] relative">
      <div className="px-5 pt-5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#CCFF00]">
        // IMAGE PREVIEW · watermarked · buy to unlock full-resolution
      </div>
      <img
        data-testid={PREVIEW.image}
        src={src}
        alt={product.title}
        draggable={false}
        onContextMenu={(e) => e.preventDefault()}
        className="w-full block mt-3 select-none"
        style={{ WebkitUserDrag: "none" }}
      />
    </div>
  );
}

function formatSize(n) {
  if (!n && n !== 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(2)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}
